import { NavLink } from "react-router-dom";
import { X } from "lucide-react";
import { navigationGroupsForEnvironment } from "@/config/navigation";
import { CURRENT_APP_ENVIRONMENT, isPreviewEnvironment } from "@/config/preview";
import { useI18n } from "@/i18n/I18nProvider";
import { cn } from "@/lib/cn";
import { useAuth } from "@/auth/AuthProvider";

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

export const Sidebar = ({ mobileOpen, onClose }: SidebarProps) => {
  const { t } = useI18n();
  const { hasAnyPermission } = useAuth();
  const preview = isPreviewEnvironment(CURRENT_APP_ENVIRONMENT);
  const navigationGroups = navigationGroupsForEnvironment(CURRENT_APP_ENVIRONMENT);

  return (
    <>
      {mobileOpen && (
        <div
          data-testid="sidebar-overlay"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-slate-950/50 lg:hidden"
        />
      )}
      <aside
        data-testid="sidebar"
        className={cn(
          "fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-border bg-card transition-transform duration-200 ease-out lg:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-4">
          <div className="flex items-center gap-2.5">
            <img
              src="/rockfall-logo.png"
              alt="Rockfall"
              className="h-8 w-8 object-contain"
               />
            <span className="leading-tight">
              <span className="block text-sm font-bold tracking-tight">{t.appName}</span>
              <span className="block text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                {t.appSuffix}
              </span>
            </span>
          </div>
          <button
            type="button"
            data-testid="sidebar-close-button"
            onClick={onClose}
            className="ops-focus rounded-sm p-1 text-muted-foreground lg:hidden"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          {navigationGroups.map((group) => ({
            ...group,
            items: group.items.filter((item) => hasAnyPermission(...item.permissions)),
          })).filter((group) => group.items.length > 0).map((group) => (
            <div key={group.key} className="mb-5">
              <p className="ops-label px-2 pb-2">{t.nav[group.key]}</p>
              <ul className="space-y-0.5">
                {group.items.map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === "/"}
                      data-testid={item.testId}
                      onClick={onClose}
                      className={({ isActive }) =>
                        cn(
                          "ops-focus group flex items-center gap-2.5 rounded-sm border-l-2 px-2.5 py-2 text-sm transition-colors duration-150",
                          isActive
                            ? "border-l-[hsl(var(--accent))] bg-[hsl(var(--surface-raised))] font-semibold text-foreground"
                            : "border-l-transparent text-muted-foreground hover:bg-[hsl(var(--surface-raised))] hover:text-foreground",
                        )
                      }
                    >
                      <item.icon className="h-[18px] w-[18px] shrink-0" />
                      <span className="truncate">{t.nav[item.key]}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-border px-4 py-3">
          <p className="font-mono text-[11px] text-muted-foreground">
            {preview ? "ROC PREVIEW · P3" : `ROCKFALL OPS · ${t.common.phase}`}
          </p>
        </div>
      </aside>
    </>
  );
};
